// UTM / campaign attribution capture — pure, React-free so it runs at app
// startup (main.tsx) before React mounts and for LOGGED-OUT landing visitors.
//
// A marketing link (https://trybeup.com/?utm_source=facebook&utm_campaign=launch
// &utm_content=day_three_wins) is opened by brand-new people who then sign up.
// We stash the FIRST-TOUCH campaign params so they survive the logged-out →
// signup → authenticated round-trip; trackEvent then tags every event (esp.
// signup_completed) with the channel, so the admin Launch Funnel + PostHog can
// attribute a signup — and downstream activation — back to the channel and even
// the specific video (utm_content).
//
// We deliberately DO NOT strip the params from the URL (unlike the coach code):
// PostHog's autocapture reads utm_* off the pageview it fires on init, which
// happens after this runs.

export const UTM_STORAGE_KEY = "trybeup_utm";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
// Ad-click ids — handy for reconciling against the ad platforms later.
const CLICK_ID_KEYS = ["gclid", "fbclid", "ttclid"] as const;

export interface StoredUtm {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  capturedAt?: string;
}

/**
 * Read utm_* / click-ids from the current URL and stash them (first-touch:
 * never overwrite an existing capture). Idempotent; safe to call anywhere.
 */
export function captureUtmFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const found: Record<string, string> = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v && v.trim()) found[k] = v.trim().slice(0, 120);
    }
    for (const k of CLICK_ID_KEYS) {
      const v = params.get(k);
      if (v && v.trim()) found[k] = v.trim().slice(0, 250);
    }
    if (Object.keys(found).length === 0) return;
    // First-touch attribution — the channel that first brought them wins.
    if (localStorage.getItem(UTM_STORAGE_KEY)) return;
    found.capturedAt = new Date().toISOString();
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
  } catch {
    /* ignore — attribution is best-effort */
  }
}

/** The stored first-touch UTM, or null. */
export function getStoredUtm(): StoredUtm | null {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredUtm) : null;
  } catch {
    return null;
  }
}

/**
 * A compact channel tag to merge into an analytics event's metadata — only the
 * fields that matter for the funnel, only when present. Empty object if no UTM
 * was captured (organic/direct), so it never adds noise.
 */
export function channelTag(): Record<string, string> {
  const u = getStoredUtm();
  if (!u) return {};
  const tag: Record<string, string> = {};
  if (u.utm_source) tag.utm_source = u.utm_source;
  if (u.utm_medium) tag.utm_medium = u.utm_medium;
  if (u.utm_campaign) tag.utm_campaign = u.utm_campaign;
  if (u.utm_content) tag.utm_content = u.utm_content;
  return tag;
}
